import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, LogOut, Mail, RefreshCw } from 'lucide-react';
import { useAiTrainerAuth } from '@/contexts/AiTrainerAuthContext';

/**
 * Shown to trainers whose account is `status=pending` — they can sign
 * in but cannot upload yet. The screen explains what's happening,
 * offers a "re-check" button (calls /v1/auth/refresh), and a sign-out.
 */
const PendingPage: React.FC = () => {
  const { user, refresh, logout, busy } = useAiTrainerAuth();
  const navigate = useNavigate();

  const recheck = async () => {
    const fresh = await refresh();
    if (fresh?.status === 'active') navigate('/library', { replace: true });
  };

  return (
    <div
      className="min-h-screen w-full bg-slate-50 px-5 py-10 sm:py-16"
      style={{ fontFamily: "'Afacad Flux', system-ui, sans-serif" }}
    >
      <div className="mx-auto max-w-lg">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1a5276] to-[#0e9172] px-6 py-7 text-white shadow-[0_14px_32px_rgba(15,82,118,0.26)] sm:px-8 sm:py-9">
          <span className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-white/10 blur-md" />
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/22 bg-white/20 px-3 py-1 text-[10.5px] font-extrabold uppercase tracking-[0.14em] backdrop-blur">
            <Clock size={11} /> Awaiting approval
          </span>
          <h1
            className="mt-4 leading-tight"
            style={{ fontFamily: "'Instrument Serif', serif", fontSize: 'clamp(22px, 5vw, 30px)' }}
          >
            Hi {user?.full_name?.split(' ')[0] || 'there'} — you're <em className="not-italic italic text-[#5ecdb0]">almost</em> in.
          </h1>
          <p className="mt-2 text-[13.5px] leading-relaxed opacity-92">
            Your trainer account is registered but an administrator hasn't
            approved it yet. You'll get an email at <strong>{user?.email}</strong> the
            moment it's activated — usually within one working day.
          </p>
        </div>

        <div className="mt-5 grid gap-3">
          <Card icon={<Mail size={16} />} title="Need a faster review?">
            Reply to your registration email or write to{' '}
            <a href="mailto:ai@mutabletech.co.ke" className="font-bold text-[#1a5276] hover:underline">
              ai@mutabletech.co.ke
            </a>{' '}
            with the institution you're uploading for.
          </Card>

          <Card icon={<Clock size={16} />} title="What you can do now">
            Browse the public corpus library (read-only) and prepare your past papers in PDF form — 30 MB max each.
          </Card>
        </div>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={recheck}
            disabled={busy}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#1a5276] px-4 py-2.5 text-[13px] font-bold text-white shadow-[0_6px_16px_rgba(26,82,118,0.25)] hover:bg-[#15436a] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw size={13} className={busy ? 'animate-spin' : ''} />
            Re-check status
          </button>
          <button
            type="button"
            onClick={() => { logout(); navigate('/login', { replace: true }); }}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-[13px] font-bold text-slate-600 hover:bg-slate-100"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

const Card: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className="flex items-start gap-3">
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#0e9172]/10 text-[#0e9172]">
        {icon}
      </span>
      <div>
        <p className="m-0 text-[14px] font-bold text-slate-900">{title}</p>
        <p className="m-0 mt-0.5 text-[12.5px] leading-relaxed text-slate-500">
          {children}
        </p>
      </div>
    </div>
  </div>
);

export default PendingPage;
